import {makeAutoObservable, reaction} from 'mobx';
import {type UIStore} from '.';

import CommonApi from '../../api/phases/commonApi';
import {CUSTOMER_PORTAL_URL} from '../../config';
import {actions, categories, pushEvent} from '../../helpers/analyticsEvents';
import phaseList from '../structures/phaseList';
import {serializable} from '../sync/serialization';

const QUOTES_PHASE = 3;

/** Strip any query string or hash that may have been persisted into a route value. */
function sanitizeRoute(route: string): string {
    return route.split('?')[0].split('#')[0];
}

class ProgressStore {
    parentStore: UIStore;

    @serializable()
    phase = 0;

    @serializable()
    percent = 0;

    @serializable()
    slide = 0;

    @serializable()
    currentSlideName = '';

    @serializable()
    slidesCounts = [6];

    @serializable()
    initialSlide = 0;

    @serializable()
    loading = false;

    @serializable()
    route = phaseList[0].route;

    @serializable()
    resettingQuote = false;

    @serializable()
    showReferralSourceQuestion = true;

    @serializable()
    isShowReferralSourceQuestion = false;

    constructor(parentStore: UIStore) {
        makeAutoObservable(this);
        this.parentStore = parentStore;

        // Clean any query params that may have been persisted into route (e.g. _gl from GA4).
        // This runs as a reaction so it also catches values loaded from localStorage after construction.
        reaction(
            () => this.route,
            (route) => {
                const clean = sanitizeRoute(route);
                if (clean !== route) {
                    this.route = clean;
                }
            },
            {fireImmediately: true},
        );

        reaction(
            () => this.phase,
            (cur, prev) => {
                if (cur === 3 && prev === 2) pushEvent(categories.preQuotes, actions.pageView);
            },
        );
    }

    initializeRoute(_queryParams: string): void {
        // No-op: query params should not be baked into the route.
        // They are preserved via the URL and sessionStorage instead.
    }

    setSlide(slide: number): void {
        this.slide = slide;
    }

    setLoading(val: boolean): void {
        this.loading = val;
    }

    updProgressBar(progress: {percent: number; phase: number; slide: number}): void {
        const {percent, phase, slide} = progress;
        this.phase = phase;
        this.percent = percent;
        this.slide = slide;
        this.route = phaseList[!!phase ? phase - 1 : 0].route;
    }

    setProgressBar(progress: {percent?: number; phase?: number; slide?: number; route?: string}): void {
        const {percent, phase, slide, route} = progress;
        const un = (val, defaultVal) => val ?? defaultVal;
        this.phase = Math.max(0, un(phase, this.phase));
        this.percent = un(percent, this.percent);
        this.slide = un(slide, this.slide);
        this.route = route || phaseList?.[this.phase]?.route || this.route;
    }

    toNextPhase(): void {
        if (this.phase == QUOTES_PHASE) {
            // if they clicked next step, direct them to customer portal with email as a parameter. This will
            // show a different text and pre-fill the email field as well.

            // Send request for validation token to Athena, wait for response, and redirect
            // Allow Athena to define the URL to provide consistency through environments
            CommonApi.getPortalRedirect(
                this.parentStore.parentStore.userData.email,
                this.parentStore.parentStore.userData.customerId,
            )
                .then((url: string) => {
                    window.location.href = url;
                })
                .catch(() => {
                    window.location.href =
                        CUSTOMER_PORTAL_URL + '?email=' + this.parentStore.parentStore.userData.email;
                });
        } else if (this.phase < phaseList.length) {
            const nextRoute = phaseList[this.phase + 1]?.route;
            if (nextRoute && !nextRoute.includes('undefined')) {
                this.setLoading(true);
                this.initialSlide = 0;
                this.slidesCounts[this.phase] = this.slide;
                this.slide = 0;
                this.phase = this.phase + 1;
                this.route = nextRoute;
                this.percent = 0.05;
            }
        }
    }

    toPreviousPhase(): void {
        if (this.phase > 0) {
            const previousRoute = phaseList[this.phase - 1]?.route;
            if (previousRoute) {
                this.setLoading(true);
                this.initialSlide = this.slidesCounts[this.phase - 1];
                this.slide = this.slidesCounts[this.phase - 1];
                this.phase -= 1;
                this.route = previousRoute;
                this.percent = 0.95;
            }
        }
    }

    toNextSlide(): void {
        this.slide = this.slide + 1;
    }

    toPreviousSlide(): void {
        this.slide = this.slide - 1;
    }

    setCurrentSlideName(slide: string): void {
        this.currentSlideName = slide;
    }

    updShowReferralSourceQuestion(val: boolean): void {
        this.isShowReferralSourceQuestion = val;
    }
}

export default ProgressStore;
